import { useEffect, useRef, useState } from 'react';
import { api, CallFile } from '../../api';
import { useCall } from './CallProvider';
import { IconClip, IconSend } from '../../components/icons';

/*
 * Переписка внутри звонка (спека `2026-09-11-call-chat-design.md`).
 *
 * Лента и поле ввода собраны видом чата (`.ch-row`, `.ch-bubble`), но своим
 * кодом: компоненты чата завязаны на ChatProvider, которого у гостя нет.
 * Пишут все участники, включая гостя; после конца звонка лента остаётся
 * историей у карточки звонка.
 */

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

export function CallChat() {
  const call = useCall();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<CallFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const feedRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const token = call.linkToken;
  const fileUrl = (id: number) =>
    call.isGuest && token
      ? api.roomMessageFileUrl(token, id)
      : call.callId
        ? api.callMessageFileUrl(call.callId, id)
        : '';

  // Открыли панель — забрали историю и обнулили счётчик непрочитанного.
  useEffect(() => {
    call.markChatRead();
    void call.loadMessages().catch(() => setError('Переписка не открылась'));
    // намеренно один раз на открытие панели
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Новое сообщение прокручивает ленту вниз: в звонке читают последнее.
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [call.messages.length]);

  // Пока панель открыта, непрочитанного нет.
  useEffect(() => {
    if (call.unread) call.markChatRead();
  }, [call, call.unread]);

  const send = () => {
    const body = text.trim();
    if ((!body && !files.length) || busy) return;
    setBusy(true);
    setError('');
    call
      .sendMessage(body, files.map((f) => f.id))
      .then(() => {
        setText('');
        setFiles([]);
      })
      .catch(() => setError('Сообщение не ушло'))
      .finally(() => setBusy(false));
  };

  const attach = (file: File) => {
    setError('');
    call
      .uploadMessageFile(file)
      .then((f) => setFiles((list) => [...list, f]))
      .catch((e: Error) => setError(e.message || 'Файл не загрузился'));
  };

  return (
    <div className="call-chat">
      {/* Подложка фирменным паттерном — как в чате приложения (3.24). */}
      <div className="call-chat-feed" ref={feedRef}>
        <div className="chat-pat" aria-hidden />
        {!call.messages.length && <div className="call-chat-empty">Здесь переписка этого звонка</div>}
        {call.messages.map((m) => {
          const own = m.author.id === call.me;
          return (
            <div key={m.id} className={`ch-row${own ? ' own' : ''}`}>
              <div className="ch-bubble-wrap">
                <div className="ch-bubble">
                  {!own && (
                    <div className="ch-bubble-h">
                      {m.author.name}
                      {m.author.kind === 'guest' && ' · гость'}
                    </div>
                  )}
                  {m.text && <div className="ch-msg-t">{m.text}</div>}
                  {m.files.map((f) => (
                    <a key={f.id} className="call-chat-file" href={fileUrl(f.id)} target="_blank" rel="noreferrer">
                      <IconClip />
                      <span>{f.name}</span>
                    </a>
                  ))}
                  <div className="ch-bubble-f">{timeOf(m.at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!!files.length && (
        <div className="call-chat-files">
          {files.map((f) => (
            <span key={f.id} className="tag">
              {f.name}
            </span>
          ))}
        </div>
      )}
      {error && <div className="call-chat-error">{error}</div>}

      <div className="call-chat-bar">
        <button
          type="button"
          className="ibtn"
          onClick={() => fileRef.current?.click()}
          title="Вложение"
          aria-label="Вложение"
        >
          <IconClip />
        </button>
        <textarea
          className="textarea call-chat-input"
          rows={1}
          placeholder="Сообщение"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter отправляет, Shift+Enter переносит строку — как в чате.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button
          type="button"
          className="ibtn send"
          onClick={send}
          disabled={busy || (!text.trim() && !files.length)}
          title="Отправить"
          aria-label="Отправить"
        >
          <IconSend />
        </button>
        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) attach(file);
          }}
        />
      </div>
    </div>
  );
}
